import { z } from 'zod';

const ObjectIdHexSchema = z
  .string()
  .regex(/^[0-9a-fA-F]{24}$/, 'expected 24-char Mongo ObjectId hex');

const EventEnvelopeBase = z.object({
  eventId: z.uuid(),
  eventVersion: z.literal(1),
  occurredAt: z.iso.datetime({ offset: true }),
});

export const MembershipEventTypeSchema = z.enum([
  'membership.created',
  'membership.removed',
  'membership.suspended',
]);

export type MembershipEventType = z.infer<typeof MembershipEventTypeSchema>;

export const MembershipEventSchema = EventEnvelopeBase.extend({
  eventType: MembershipEventTypeSchema,
  orgId: ObjectIdHexSchema,
  membership: z.object({
    userId: z.string().min(1),
    role: z.string().min(1).optional(),
    source: z.string().min(1).optional(),
    status: z.string().min(1).optional(),
  }),
  user: z.object({
    cognitoSub: z.string().min(1),
    name: z.string().default(''),
    email: z.string().default(''),
  }),
});

export type MembershipEvent = z.infer<typeof MembershipEventSchema>;

export const OrgCreatedEventSchema = EventEnvelopeBase.extend({
  eventType: z.literal('org.created'),
  orgId: ObjectIdHexSchema,
  org: z.object({
    name: z.string().min(1),
    slug: z.string().min(1),
  }),
});

export type OrgCreatedEvent = z.infer<typeof OrgCreatedEventSchema>;

/**
 * Cross-org user profile change. Emitted when the operator updates their
 * own profile (e.g. PATCH /me). Has no `orgId` because the same user can
 * be a member of multiple orgs and the name needs to propagate to every
 * one of them — the consumer fans out by matching on `cognitoSub`.
 *
 * Ordering is per-user, so the publisher uses `cognitoSub` as the FIFO
 * MessageGroupId.
 */
export const UserProfileUpdatedEventSchema = EventEnvelopeBase.extend({
  eventType: z.literal('user.profile_updated'),
  user: z.object({
    cognitoSub: z.string().min(1),
    name: z.string().default(''),
    email: z.string().default(''),
  }),
});

export type UserProfileUpdatedEvent = z.infer<typeof UserProfileUpdatedEventSchema>;

export const AgentProviderDeletedEventSchema = EventEnvelopeBase.extend({
  eventType: z.literal('agent_provider.deleted'),
  orgId: ObjectIdHexSchema,
  provider: z.object({
    id: z.guid(),
    agentExternalRefId: z.guid(),
    name: z.string().min(1),
  }),
});

export type AgentProviderDeletedEvent = z.infer<typeof AgentProviderDeletedEventSchema>;

export const AccountsEventTypeSchema = z.enum([
  ...MembershipEventTypeSchema.options,
  'org.created',
  'user.profile_updated',
  'agent_provider.deleted',
]);

export type AccountsEventType = z.infer<typeof AccountsEventTypeSchema>;
